import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useActiveContextReady } from '@/hooks/useActiveContextReady';
import { useAuth } from '@/hooks/useAuth';
import { apiErrorMessage } from '@/utils/apiError';
import { vpnKeys } from '@/features/vpn/hooks';
import { fetchSessionFlows, fetchVpnSettings, lookupFlows, setFlowRetention, type FlowLookup } from './api';

export const flowKeys = {
  sessionFlows: (sessionId: string, page: number, pageSize: number) =>
    [...vpnKeys.all, 'flows', sessionId, page, pageSize] as const,
  lookup: (query: FlowLookup) => [...vpnKeys.all, 'lookup', query] as const,
  settings: () => [...vpnKeys.all, 'settings'] as const,
};

function useReady(): boolean {
  const { user } = useAuth();
  const contextReady = useActiveContextReady();
  return !!user?.organizationId && contextReady;
}

export function useSessionFlows(sessionId: string, page: number, pageSize: number) {
  const ready = useReady();
  return useQuery({
    queryKey: flowKeys.sessionFlows(sessionId, page, pageSize),
    queryFn: () => fetchSessionFlows(sessionId, page, pageSize),
    enabled: ready,
    placeholderData: keepPreviousData,
  });
}

/** Runs once `query` is set: a lookup is asked for, not typed into. */
export function useFlowLookup(query: FlowLookup | null) {
  const ready = useReady();
  return useQuery({
    queryKey: flowKeys.lookup(query ?? { regionId: '', at: '' }),
    queryFn: () => lookupFlows(query as FlowLookup),
    enabled: ready && query !== null,
  });
}

export function useVpnSettings() {
  const ready = useReady();
  return useQuery({ queryKey: flowKeys.settings(), queryFn: fetchVpnSettings, enabled: ready });
}

export function useSetFlowRetention() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setFlowRetention,
    onSuccess: (settings) => {
      queryClient.setQueryData(flowKeys.settings(), settings);
      toast.success('Retention saved');
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the retention")),
  });
}
